import React, { useState, useRef } from 'react';
import _merge from 'lodash/merge';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext } from 'common';

import ChevronUpIcon from '@nokia-csf-uxr/ccfk-assets/ChevronUpIcon';
import ChevronDownIcon from '@nokia-csf-uxr/ccfk-assets/ChevronDownIcon';
import List, { ListItemBasic, ListProps } from 'components/List';
import { getElevationStyle } from 'components/Elevation';
import IconButton from 'components/IconButton';

import KEY_CODES from 'common/KeyCodes';

import Styled from './styles';

const LIST_STYLE = ({ background }) => ({
  background,
  boxShadow: 'none',
  overflow: 'hidden'
});

const SHARED_ITEM_STYLE = {
  justifyContent: 'center',
  padding: 0,
  marginBottom: Tokens.SPACING.SPACING_12,
};

const ITEM_STYLE = ({ content, background }) => ({
  ...SHARED_ITEM_STYLE,
  color: content,
  background,
  ...Tokens.TYPOGRAPHY.TITLE_24
});

const ITEM_SELECTED_STYLE = ({ content, background }) => ({
  ...SHARED_ITEM_STYLE,
  ...Tokens.TYPOGRAPHY.TITLE_24,
  color: content,
  background,
  fontFamily: Tokens.FONT_FAMILY.NOKIA_PURE_TEXT_BOLD
});

const { ENTER_KEY, SPACE_KEY } = KEY_CODES;
const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);
type YearOnChange = (arg: { year: string, event: React.MouseEvent | React.KeyboardEvent }) => void;
export interface CalendarYearPickerProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /**
   * The visible date's year number, e.g.: 2021.
   *  Read README tab for more information about `Visible date` and `Selected Date`. */
  visibleYear: number;
  /** The number of items on the year list.  */
  numberOfYearItems?: number;
  /** To overwrite the years options. */
  years?: {
    label: string,
    value: number
  }[];
  /** To overwrite the array of the year items. By default, `CalendarYearPicker` renders years on the list considering the `numberOfYearItems` prop. */
  yearsItems?: React.ReactNode[];
  /** Callback fired when the value is changed. It returns the value of the item and the triggering event, `function({ event, year }) => void ` */
  onChange?: YearOnChange;
  /** To overwrite the `<List />` props. See List component documentation for details. */
  listProps?: Partial<ListProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * <CalendarYearPicker />, any other props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const CalendarYearPicker = React.forwardRef<HTMLElement, CalendarYearPickerProps>((props: CalendarYearPickerProps, ref) => getContext(({ theme }) => {
  const {
    numberOfYearItems,
    yearsItems,
    years: yearsProps,
    listProps,
    visibleYear,
    theme: themeProp,
    onChange,
    ...otherProps
  } = props;

  const resolvedTheme = _merge(
    {},
    theme.CALENDAR.YEAR_PICKER,
    themeProp
  );

  const SCROLL_BY_VALUE = direction => ({
    top: direction * 130,
    left: 0,
    behavior: 'smooth'
  });

  const listRef = useRef(null);

  const [focusedItemIndex, setFocusedItemIndex] = useState(undefined);

  const handleItemEvent = ({ selectedYear, event }) => {
    const { type } = event;
    if (type === 'keydown') {
      if (isSelectionKeyPressed(event.key)) {
        onChange && onChange({ event, year: selectedYear });
      }
    } else if (type === 'click') {
      onChange && onChange({ event, year: selectedYear });
    }
  };

  const getResolvedYears = () => {
    if (yearsProps) {
      return yearsProps;
    }
    if (numberOfYearItems === 1 || numberOfYearItems === 0) {
      return [{ value: visibleYear, label: visibleYear }];
    }
    return Array.from(Array(numberOfYearItems).keys()).map((index) => {
      const yr = visibleYear + ((-1 * Math.floor(numberOfYearItems / 2)) + index);
      return ({
        value: yr,
        label: yr
      });
    });
  };

  return (
    <Styled.CalendarYearPicker
      style={{
        backgroundColor: getElevationStyle({ elevationIndex: 24 }).backgroundColor
      }}
      ref={ref}
      {...otherProps}
    >
      <IconButton onClick={() => { listRef.current.scrollBy(SCROLL_BY_VALUE(-1)); }}>
        <ChevronUpIcon />
      </IconButton>
      <Styled.CalendarYearPickerListContainer>
        <List ref={listRef} style={(LIST_STYLE(resolvedTheme.LIST))} elevationProps={{ transparent: true, elevationIndex: 0 }}>
          {yearsItems || getResolvedYears().map((yearItem, index) => {
            const isSelected = yearItem.value === visibleYear;
            const isFocused = index === focusedItemIndex;
            const isAutoFocus = focusedItemIndex === undefined && isSelected;
            return (
              <ListItemBasic
                onClick={event => handleItemEvent({ selectedYear: yearItem.value, event })}
                onKeyDown={event => handleItemEvent({ selectedYear: yearItem.value, event })}
                onFocus={() => setFocusedItemIndex(index)}
                focusItem={isFocused}
                autoFocus={isAutoFocus}
                style={isSelected
                  ? ITEM_SELECTED_STYLE(resolvedTheme.ITEM.selected)
                  : ITEM_STYLE(resolvedTheme.ITEM)
                }
                key={yearItem.label}
              >
                {yearItem.label}
              </ListItemBasic>
            );
          })}
        </List>
      </Styled.CalendarYearPickerListContainer>
      <IconButton onClick={() => { listRef.current.scrollBy(SCROLL_BY_VALUE(1)); }}>
        <ChevronDownIcon />
      </IconButton>
    </Styled.CalendarYearPicker>
  );
}));

CalendarYearPicker.displayName = 'CalendarYearPicker';

CalendarYearPicker.defaultProps = {
  yearsItems: undefined,
  numberOfYearItems: 50,
  years: undefined,
  onChange: undefined,
  listProps: {},
  theme: {}
};

export default CalendarYearPicker;
